import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  Archive,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { basecampApi, BasecampProjectPreview, ImportMode } from '@/lib/api';

interface Props {
  accountId: string;
  onStartImport: (selections: Map<number, ImportMode>, projectOrgMap: Map<number, string>) => void;
}

type StatusFilter = 'all' | 'new' | 'imported' | 'has_changes';

export function BasecampProjectBrowser({ accountId, onStartImport }: Props) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selections, setSelections] = useState<Map<number, ImportMode>>(new Map());
  const [collapsedOrgs, setCollapsedOrgs] = useState<Set<string>>(new Set());
  const [orgOverrides, setOrgOverrides] = useState<Map<number, string>>(new Map());

  // Fetch projects
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['basecamp-projects', accountId],
    queryFn: () => basecampApi.getProjects(accountId),
  });

  const projects = data?.projects || [];

  // Filter and group projects
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        if (
          !p.name.toLowerCase().includes(searchLower) &&
          !p.parsedProjectName.toLowerCase().includes(searchLower) &&
          !p.parsedOrgName.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }
      // Status filter
      if (statusFilter !== 'all' && p.importStatus !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [projects, search, statusFilter]);

  const groupedProjects = useMemo(() => {
    const groups = new Map<string, BasecampProjectPreview[]>();
    for (const project of filteredProjects) {
      const orgName = project.parsedOrgName || 'No Organization';
      if (!groups.has(orgName)) {
        groups.set(orgName, []);
      }
      groups.get(orgName)!.push(project);
    }
    // Sort groups alphabetically
    return new Map([...groups.entries()].sort(([a], [b]) => a.localeCompare(b)));
  }, [filteredProjects]);

  // Known org names for autocomplete (from projects that have a parsed org)
  const knownOrgNames = useMemo(() => {
    const names = new Set<string>();
    for (const p of projects) {
      if (p.parsedOrgName) names.add(p.parsedOrgName);
    }
    return Array.from(names).sort();
  }, [projects]);

  // Selection helpers
  const toggleSelection = (basecampId: number, project: BasecampProjectPreview) => {
    const next = new Map(selections);
    if (next.has(basecampId)) {
      next.delete(basecampId);
    } else {
      // Default mode based on status
      const defaultMode: ImportMode = project.importStatus === 'new' ? 'sync_new' : 'sync_new';
      next.set(basecampId, defaultMode);
    }
    setSelections(next);
  };

  const setImportMode = (basecampId: number, mode: ImportMode) => {
    const next = new Map(selections);
    next.set(basecampId, mode);
    setSelections(next);
  };

  const selectAllNew = () => {
    const next = new Map(selections);
    for (const project of filteredProjects) {
      if (project.importStatus === 'new') {
        next.set(project.basecampId, 'sync_new');
      }
    }
    setSelections(next);
  };

  const deselectAll = () => {
    setSelections(new Map());
  };

  const toggleOrg = (orgName: string) => {
    const next = new Set(collapsedOrgs);
    if (next.has(orgName)) {
      next.delete(orgName);
    } else {
      next.add(orgName);
    }
    setCollapsedOrgs(next);
  };

  const setOrgOverride = (basecampId: number, orgName: string) => {
    const next = new Map(orgOverrides);
    if (orgName) {
      next.set(basecampId, orgName);
    } else {
      next.delete(basecampId);
    }
    setOrgOverrides(next);
  };

  const selectedCount = selections.size;

  // Status badge component
  const StatusBadge = ({ status }: { status: BasecampProjectPreview['importStatus'] }) => {
    switch (status) {
      case 'new':
        return <span className="badge bg-success/10 text-success text-xs">New</span>;
      case 'imported':
        return <span className="badge bg-grey-100 text-grey-600 text-xs">Imported</span>;
      case 'has_changes':
        return <span className="badge bg-amber-100 text-amber-700 text-xs">Has Updates</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-grey-400 animate-spin mr-2" />
        <span className="text-sm text-grey-500">Loading projects from Basecamp...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-primary">Failed to load projects</p>
            <p className="text-sm text-grey-600 mt-1">
              {error instanceof Error ? error.message : 'An error occurred'}
            </p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
          <input
            type="text"
            className="input pl-9"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="input w-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="all">All Status</option>
          <option value="new">New Only</option>
          <option value="imported">Already Imported</option>
          <option value="has_changes">Has Updates</option>
        </select>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-2 text-sm">
        <button className="text-accent hover:underline" onClick={selectAllNew}>
          Select All New
        </button>
        <span className="text-grey-300">|</span>
        <button className="text-accent hover:underline" onClick={deselectAll}>
          Deselect All
        </button>
        <span className="text-grey-400 ml-auto">
          {filteredProjects.length} projects found
        </span>
      </div>

      {/* Project list */}
      <div className="border border-grey-200 rounded-lg max-h-[500px] overflow-y-auto">
        {groupedProjects.size === 0 ? (
          <div className="p-8 text-center text-grey-500 text-sm">
            No projects found matching your filters.
          </div>
        ) : (
          Array.from(groupedProjects.entries()).map(([orgName, orgProjects]) => (
            <div key={orgName} className="border-b border-grey-100 last:border-b-0">
              {/* Org header */}
              <button
                className="w-full flex items-center gap-2 px-4 py-2.5 bg-grey-50 hover:bg-grey-100 text-left transition-colors"
                onClick={() => toggleOrg(orgName)}
              >
                {collapsedOrgs.has(orgName) ? (
                  <ChevronRight className="w-4 h-4 text-grey-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-grey-400" />
                )}
                <span className="font-medium text-sm text-secondary">{orgName}</span>
                <span className="text-xs text-grey-400">({orgProjects.length})</span>
              </button>

              {/* Projects in org */}
              {!collapsedOrgs.has(orgName) && (
                <div>
                  {orgProjects.map((project) => {
                    const isSelected = selections.has(project.basecampId);
                    const currentMode = selections.get(project.basecampId);

                    return (
                      <div
                        key={project.basecampId}
                        className={`flex items-center gap-3 px-4 py-2.5 border-t border-grey-50 hover:bg-grey-50/50 transition-colors ${
                          isSelected ? 'bg-accent/5' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-grey-300 text-accent focus:ring-accent"
                          checked={isSelected}
                          onChange={() => toggleSelection(project.basecampId, project)}
                        />

                        {/* Project info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-secondary truncate">
                              {project.parsedProjectName}
                            </span>
                            {project.archived && (
                              <Archive className="w-3.5 h-3.5 text-grey-400" />
                            )}
                            <StatusBadge status={project.importStatus} />
                          </div>
                          <div className="flex items-center gap-3 text-xs text-grey-400 mt-0.5">
                            {project.itemCounts.todolists > 0 && (
                              <span>{project.itemCounts.todolists} lists</span>
                            )}
                            {project.itemCounts.topics > 0 && (
                              <span>{project.itemCounts.topics} messages</span>
                            )}
                            {project.itemCounts.documents > 0 && (
                              <span>{project.itemCounts.documents} docs</span>
                            )}
                          </div>
                        </div>

                        {/* Org assignment for orphaned projects */}
                        {isSelected && !project.parsedOrgName && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              list="org-names-datalist"
                              className="text-xs border border-grey-200 rounded px-2 py-1 bg-white w-36"
                              placeholder="Assign org..."
                              value={orgOverrides.get(project.basecampId) || ''}
                              onChange={(e) => setOrgOverride(project.basecampId, e.target.value)}
                            />
                          </div>
                        )}

                        {/* Import mode selector (only for selected + previously imported) */}
                        {isSelected && project.importStatus !== 'new' && (
                          <select
                            className="text-xs border border-grey-200 rounded px-2 py-1 bg-white"
                            value={currentMode}
                            onChange={(e) =>
                              setImportMode(project.basecampId, e.target.value as ImportMode)
                            }
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="sync_new">Sync New Items</option>
                            <option value="replace">Full Replace</option>
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Datalist for org name autocomplete */}
      <datalist id="org-names-datalist">
        {knownOrgNames.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>

      {/* Action bar */}
      <div className="flex items-center justify-between pt-2">
        <p className="text-sm text-grey-500">
          {selectedCount > 0
            ? `${selectedCount} project${selectedCount > 1 ? 's' : ''} selected`
            : 'Select projects to import'}
        </p>
        <Button
          disabled={selectedCount === 0}
          onClick={() => {
            // Build map of projectId -> orgName for selected projects
            const projectOrgMap = new Map<number, string>();
            for (const p of projects) {
              if (selections.has(p.basecampId)) {
                if (p.parsedOrgName) {
                  projectOrgMap.set(p.basecampId, p.parsedOrgName);
                } else if (orgOverrides.has(p.basecampId)) {
                  // Include user-assigned org overrides for "No Organization" projects
                  projectOrgMap.set(p.basecampId, orgOverrides.get(p.basecampId)!);
                }
              }
            }
            onStartImport(selections, projectOrgMap);
          }}
        >
          Import {selectedCount > 0 ? `${selectedCount} Project${selectedCount > 1 ? 's' : ''}` : 'Selected'}
        </Button>
      </div>
    </div>
  );
}
